import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';

import { PendingRecurringEvent, RecurringEventFlyerDetails } from '@shared/types/event';
import dayjs from 'dayjs';

import { useCurrentUser } from '@queries/UserQueries';
import { useCurrentUserScheduleSubmissionQuery } from '@queries/UserScheduleSubmissionsQueries';

import { useUpdatePendingRecurringEventMutation } from '@mutations/UserScheduleSubmissionsMutations';

import { ImageUploadField } from '@components/form/ImageUploadField';
import { Popup } from '@components/layout/Popup/Popup';

import './RecurringEventConfirmationPopup.scss';

enum RecurringEventConfirmationPopupStep {
  Confirmation = 'confirmation',
  FlyerOptions = 'flyerOptions',
  FlyerUpload = 'flyerUpload',
  Confirmed = 'confirmed',
  Reused = 'reused',
  Skipped = 'skipped',
  Declined = 'declined',
  Ended = 'ended',
}

type PendingEvent = PendingRecurringEvent['event'];

const getEventLabels = (event: PendingEvent) => {
  const start = event.startTime ? dayjs(`${event.startDate} ${event.startTime}`) : undefined;

  return {
    dayLabel: dayjs(event.startDate).format('dddd'),
    dateLabel: dayjs(event.startDate).format('MMM D'),
    timeLabel: start?.isValid() ? start.format('h:mm A').replace(':00', '') : undefined,
  };
};

const GeneratedEventCard = ({ event }: { event: PendingEvent }) => {
  const { dayLabel, dateLabel, timeLabel } = getEventLabels(event);

  return (
    <div className="recurring-confirm_card">
      <div className="recurring-confirm_card-when">
        {dayLabel} · {dateLabel}
      </div>
      <div className="recurring-confirm_card-title">{event.title}</div>
      <div className="recurring-confirm_card-foot">
        <div className="recurring-confirm_card-venue">
          <strong>{event.venue?.title}</strong>
          {timeLabel}
        </div>
        <div className="recurring-confirm_card-mark">Gaggl</div>
      </div>
    </div>
  );
};

interface ConfirmationStepProps {
  event: PendingEvent;
  flyerDetails: RecurringEventFlyerDetails;
  onConfirm: () => void;
  onDecline: () => void;
  onEnded: () => void;
}

const ConfirmationStep = ({
  event,
  flyerDetails,
  onConfirm,
  onDecline,
  onEnded,
}: ConfirmationStepProps) => {
  const { dayLabel } = getEventLabels(event);

  const question =
    event.title.length < 16 ? (
      <>
        Is <em>{event.title}</em> happening this {dayLabel}?
      </>
    ) : (
      <>Is this event happening on {dayLabel}?</>
    );

  return (
    <>
      <div className="recurring-confirm_eyebrow">Your weekly show</div>
      <h2 className="recurring-confirm_question">{question}</h2>
      <GeneratedEventCard event={event} />
      <div className="recurring-confirm_actions">
        <button
          className="recurring-confirm_btn recurring-confirm_btn--secondary"
          onClick={onDecline}
        >
          Not this week
        </button>
        <button
          className="recurring-confirm_btn recurring-confirm_btn--primary"
          onClick={onConfirm}
        >
          Yes, it's on
        </button>
      </div>
      <button className="recurring-confirm_link" onClick={onEnded}>
        This show has ended
      </button>
    </>
  );
};

interface FlyerOptionsStepProps {
  flyerDetails: RecurringEventFlyerDetails;
  onUploadNew: () => void;
  onReuse: () => void;
  onSkip: () => void;
}

const FlyerOptionsStep = ({
  flyerDetails,
  onUploadNew,
  onReuse,
  onSkip,
}: FlyerOptionsStepProps) => {
  const { lastFlyerUrl, lastFlyerDate } = flyerDetails;

  const lastFlyerLabel = lastFlyerDate
    ? `${dayjs(lastFlyerDate).format('MMM D')} flyer`
    : 'Last flyer';

  return (
    <>
      <div className="recurring-confirm_eyebrow">You're confirmed ✓</div>
      <h2 className="recurring-confirm_question">Got this week's flyer?</h2>
      <div className="recurring-confirm_prev">
        <span
          className="recurring-confirm_prev-thumb"
          style={lastFlyerUrl ? { backgroundImage: `url('${lastFlyerUrl}')` } : undefined}
        />
        <div>
          <div className="recurring-confirm_prev-label">Last time</div>
          <div className="recurring-confirm_prev-text">
            {lastFlyerLabel} — hidden from your listing until you choose
          </div>
        </div>
      </div>
      <div className="recurring-confirm_options">
        <button
          className="recurring-confirm_opt recurring-confirm_opt--primary"
          onClick={onUploadNew}
        >
          <span className="recurring-confirm_opt-icon">＋</span>
          <span>
            Upload new flyer
            <small>From photos or a link</small>
          </span>
        </button>
        <button className="recurring-confirm_opt" onClick={onReuse}>
          <span className="recurring-confirm_opt-icon">↺</span>
          <span>
            Same as last time
            <small>Reuse it for this week</small>
          </span>
        </button>
      </div>
      <button className="recurring-confirm_link" onClick={onSkip}>
        Skip — we'll look for it
      </button>
    </>
  );
};

interface FlyerUploadStepProps {
  event: PendingEvent;
  onSubmit: (base64Image: string | ArrayBuffer) => void;
  onBack: () => void;
}

const FlyerUploadStep = ({ event, onSubmit, onBack }: FlyerUploadStepProps) => {
  const [img, setImg] = useState<string | ArrayBuffer>();
  const pickerRef = useRef<HTMLDivElement>(null);
  const { dayLabel } = getEventLabels(event);

  // Re-runs ImageUploadField's own click handler, which opens the native/web picker
  const handleChooseAnother = () => {
    pickerRef.current?.querySelector('div')?.click();
  };

  return (
    <>
      <div className="recurring-confirm_eyebrow">New flyer</div>
      <h2 className="recurring-confirm_question">Use this for {dayLabel}?</h2>
      <div ref={pickerRef} className="recurring-confirm_upload">
        <ImageUploadField
          placeholder="Tap to choose a flyer"
          onChange={setImg}
          style={{ width: '100%', height: '100%' }}
          imgStyle={{ width: '100%', height: '100%', objectFit: 'contain' }}
          placeholderStyle={{ color: 'var(--rc-muted)' }}
        />
      </div>
      <div className="recurring-confirm_actions">
        <button
          className="recurring-confirm_btn recurring-confirm_btn--secondary"
          onClick={handleChooseAnother}
        >
          Choose another
        </button>
        <button
          className="recurring-confirm_btn recurring-confirm_btn--primary"
          disabled={!img}
          onClick={() => img && onSubmit(img)}
        >
          Use this flyer
        </button>
      </div>
      <button className="recurring-confirm_link" onClick={onBack}>
        Back
      </button>
    </>
  );
};

interface DoneStepProps {
  icon: string;
  title: string;
  subtitle: string;
  isNeutral?: boolean;
  onFinish: () => void;
}

const DoneStep = ({ icon, title, subtitle, isNeutral, onFinish }: DoneStepProps) => {
  return (
    <>
      <div className="recurring-confirm_done">
        <div
          className={`recurring-confirm_done-ring ${isNeutral ? 'recurring-confirm_done-ring--neutral' : ''}`}
        >
          {icon}
        </div>
        <h2 className="recurring-confirm_done-title">{title}</h2>
        <p className="recurring-confirm_done-sub">{subtitle}</p>
      </div>
      <button
        className="recurring-confirm_btn recurring-confirm_btn--primary recurring-confirm_btn--block"
        onClick={onFinish}
      >
        Done
      </button>
    </>
  );
};

interface RecurringEventConfirmationPopupContentProps {
  onClose: () => void;
}

const RecurringEventConfirmationPopupContent = ({
  pendingRecurringEvent,
  onClose,
}: RecurringEventConfirmationPopupContentProps & {
  pendingRecurringEvent: PendingRecurringEvent;
}) => {
  const [step, setStep] = useState(RecurringEventConfirmationPopupStep.Confirmation);
  const { event, flyerDetails } = pendingRecurringEvent;
  const { dayLabel } = getEventLabels(event);
  const { updatePendingRecurringEventAsync, isSubmitting } =
    useUpdatePendingRecurringEventMutation();
  const { currentUser } = useCurrentUser();
  const queryClient = useQueryClient();

  const handleConfirm = async () => {
    setStep(
      flyerDetails.flyerMode === 'static'
        ? RecurringEventConfirmationPopupStep.Confirmed
        : RecurringEventConfirmationPopupStep.FlyerOptions,
    );

    await updatePendingRecurringEventAsync({
      eventId: event._id,
      status: 'approved',
    });
  };

  const handleEventSkipped = async () => {
    setStep(RecurringEventConfirmationPopupStep.Declined);

    await updatePendingRecurringEventAsync({
      eventId: event._id,
      status: 'rejected',
    });
  };

  const handleEventEnded = async () => {
    setStep(RecurringEventConfirmationPopupStep.Ended);

    await updatePendingRecurringEventAsync({
      eventId: event._id,
      status: 'ended',
    });
  };

  const handleNewFlyer = async () => {
    setStep(RecurringEventConfirmationPopupStep.FlyerUpload);
  };

  const handleBackToFlyerOptions = () => {
    setStep(RecurringEventConfirmationPopupStep.FlyerOptions);
  };

  const handleFlyerUpload = async (base64Image: string | ArrayBuffer) => {
    setStep(RecurringEventConfirmationPopupStep.Confirmed);

    await updatePendingRecurringEventAsync({
      eventId: event._id,
      flyer: {
        base64Image,
      },
    });
  };

  const handleFlyerReuse = async () => {
    setStep(RecurringEventConfirmationPopupStep.Reused);

    if (flyerDetails?.previousEventId) {
      await updatePendingRecurringEventAsync({
        eventId: event._id,
        flyer: {
          previousEventId: flyerDetails.previousEventId,
        },
      });
    }
  };

  const handleFlyerSkipped = () => {
    setStep(RecurringEventConfirmationPopupStep.Skipped);
  };

  const handleFinish = () => {
    onClose();

    queryClient.invalidateQueries({
      queryKey: useCurrentUserScheduleSubmissionQuery.queryKey(currentUser?._id ?? ''),
    });
  };

  const confirmedTitle = `You're on for ${dayLabel}`;

  const renderStep = () => {
    switch (step) {
      case RecurringEventConfirmationPopupStep.Confirmation:
        return (
          <ConfirmationStep
            event={event}
            flyerDetails={flyerDetails}
            onConfirm={handleConfirm}
            onDecline={handleEventSkipped}
            onEnded={handleEventEnded}
          />
        );
      case RecurringEventConfirmationPopupStep.FlyerOptions:
        return (
          <FlyerOptionsStep
            flyerDetails={flyerDetails}
            onUploadNew={handleNewFlyer}
            onReuse={handleFlyerReuse}
            onSkip={handleFlyerSkipped}
          />
        );
      case RecurringEventConfirmationPopupStep.FlyerUpload:
        return (
          <FlyerUploadStep
            event={event}
            onSubmit={handleFlyerUpload}
            onBack={handleBackToFlyerOptions}
          />
        );
      case RecurringEventConfirmationPopupStep.Confirmed:
        return (
          <DoneStep
            icon="✓"
            title={confirmedTitle}
            subtitle="Fans can see it on the guide now."
            onFinish={handleFinish}
          />
        );
      case RecurringEventConfirmationPopupStep.Reused:
        return (
          <DoneStep
            icon="✓"
            title={confirmedTitle}
            subtitle="Reusing last week’s flyer."
            onFinish={handleFinish}
          />
        );
      case RecurringEventConfirmationPopupStep.Skipped:
        return (
          <DoneStep
            icon="✓"
            title={confirmedTitle}
            subtitle="We’ll check the venue’s posts for the flyer."
            onFinish={handleFinish}
          />
        );
      case RecurringEventConfirmationPopupStep.Declined:
        return (
          <DoneStep
            isNeutral
            icon="–"
            title="Got it — off this week"
            subtitle="We’ll ask again next week."
            onFinish={handleFinish}
          />
        );
      case RecurringEventConfirmationPopupStep.Ended:
        return (
          <DoneStep
            isNeutral
            icon="–"
            title="Removed from your calendar"
            subtitle="We won’t ask about this show again."
            onFinish={handleFinish}
          />
        );
    }
  };

  // Keyed on step so the enter animation replays on each transition
  return (
    <div key={step} className="recurring-confirm">
      {renderStep()}
    </div>
  );
};

interface RecurringEventConfirmationPopupProps extends RecurringEventConfirmationPopupContentProps {
  isVisible: boolean;
  pendingRecurringEvent?: PendingRecurringEvent;
}

const RecurringEventConfirmationPopup = ({
  isVisible,
  pendingRecurringEvent,
  ...props
}: RecurringEventConfirmationPopupProps) => {
  return (
    <Popup
      isVisible={isVisible} // height="3quarters"
      onClose={props.onClose}
    >
      <Popup.Content>
        {isVisible && pendingRecurringEvent && (
          <RecurringEventConfirmationPopupContent
            pendingRecurringEvent={pendingRecurringEvent}
            {...props}
          />
        )}
      </Popup.Content>
    </Popup>
  );
};

export const ControlledRecurringEventConfirmationPopup = () => {
  const { schedule } = useCurrentUserScheduleSubmissionQuery();
  const [readRecord, setReadRecord] = useState<Record<string, boolean>>({});

  const selectedPendingRecurringEvent = useMemo(() => {
    const unreadPendingRecurringEvents = schedule?.pendingRecurringEvents?.filter(
      ({ event }) => !readRecord[event._id],
    );

    return unreadPendingRecurringEvents?.[0];
  }, [schedule, readRecord]);

  const handleClose = () => {
    if (selectedPendingRecurringEvent?.event?._id) {
      setReadRecord((prev) => ({ ...prev, [selectedPendingRecurringEvent.event._id]: true }));
    }
  };

  return (
    <RecurringEventConfirmationPopup
      isVisible={!!selectedPendingRecurringEvent}
      pendingRecurringEvent={selectedPendingRecurringEvent}
      onClose={handleClose}
    />
  );
};
